import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Palette, Shield, Waves, Star, Check, Anchor, Sparkles } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import CategoryCard from '../components/CategoryCard';
import { testimonials } from '../data/mock';
import { getFeaturedProducts, getCategories } from '../api/api';
import { SEO, organizationSchema } from '../components/SEO';
import { imgUrl } from '../utils/imgUrl';
import { DealerForm } from '../components/DealerForm';
import { SaleCountdown } from '../components/SaleCountdown';
import SaleSpotlight from '../components/SaleSpotlight';

const Home = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsData, categoriesData] = await Promise.all([
          getFeaturedProducts(),
          getCategories()
        ]);
        setFeaturedProducts(productsData);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        title="Premium In-Pool Furniture | Tenjam SPLASH Series"
        description="Shop premium in-pool loungers, chairs, tables and stools by Tenjam SPLASH Series at Rezvani Furniture Studio. Made in the USA, 100% recyclable. Free shipping on select items."
        canonical="https://rezvanifurniturestudio.com/"
        jsonLd={organizationSchema()}
      />
      {/* Hero Section */}
      <section className="relative min-h-[80vh] sm:min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-cyan-900 via-blue-900 to-gray-900">
        {/* Hero background image — uses <img> so the browser can progressively render & lazy-decode.
            On slow connections the dark gradient above remains visible until the image finishes loading. */}
        <img
          src={imgUrl("https://customer-assets.emergentagent.com/job_furniture-studio-3/artifacts/aflm330q_SPL33100WH%20Pool_Scene_v1_v4%281%29.jpg", 1280)}
          srcSet={[640, 960, 1280, 1920].map((w) => `${imgUrl("https://customer-assets.emergentagent.com/job_furniture-studio-3/artifacts/aflm330q_SPL33100WH%20Pool_Scene_v1_v4%281%29.jpg", w)} ${w}w`).join(', ')}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/60 to-transparent" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white py-20 sm:py-0">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-5 sm:mb-6 leading-tight">
              Transform Your Pool Into a
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                Luxury Resort
              </span>
            </h1>
            <p className="text-base sm:text-xl md:text-2xl mb-6 sm:mb-8 text-gray-200 leading-relaxed">
              Premium in-pool furniture designed for ultimate comfort and style. Custom colors, built to last.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/products"
                className="group px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <span>Shop Collection</span>
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/about"
                className="px-6 sm:px-8 py-3.5 sm:py-4 bg-white/10 backdrop-blur-md text-white rounded-xl font-semibold hover:bg-white/20 transition-all duration-300 flex items-center justify-center border border-white/20"
              >
                Learn More
              </Link>
            </div>
            <SaleCountdown className="mt-6 sm:mt-8 bg-white/90 backdrop-blur-sm" />
          </div>
        </div>
      </section>

      {/* Cold Plunge Sale Spotlight (auto-hides when sale ends) */}
      <SaleSpotlight />

      {/* Categories Section */}
      <section className="py-14 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Shop by Category</h2>
            <p className="text-base sm:text-xl text-gray-600">Find the perfect furniture for your pool</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {categories.map((category) => (
              <CategoryCard key={category.id} category={category} />
            ))}
          </div>
        </div>
      </section>

      {/* Patent-Pending Ballast Stabilization System Highlight */}
      <section className="relative py-16 sm:py-24 overflow-hidden bg-gradient-to-br from-gray-900 via-slate-900 to-cyan-950 text-white">
        {/* Decorative wave pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 1440 600" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 300C240 200 480 400 720 300C960 200 1200 400 1440 300V600H0V300Z" fill="url(#waveGrad)" />
            <defs>
              <linearGradient id="waveGrad" x1="0" y1="0" x2="1440" y2="600" gradientUnits="userSpaceOnUse">
                <stop stopColor="#22d3ee" />
                <stop offset="1" stopColor="#3b82f6" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-400/30 backdrop-blur-sm mb-5 sm:mb-6">
                <Sparkles size={16} className="text-cyan-300" />
                <span className="text-xs font-semibold text-cyan-300 uppercase tracking-[0.2em]">Patent-Pending Innovation</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-5 sm:mb-6">
                The <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-400">Ballast Stabilization System</span>
              </h2>

              <p className="text-base sm:text-lg lg:text-xl text-gray-300 leading-relaxed mb-6 sm:mb-8 max-w-2xl">
                Forget outdated fill-and-drain methods. Tenjam&rsquo;s integrated, <strong className="text-white">patent-pending Ballast Stabilization System</strong> automatically locks every lounger and chair securely to your tanning ledge or Baja shelf — no sand, no water filling, no hassle.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
                {[
                  { icon: Anchor, title: 'Anchored & Stable', desc: 'Locks securely in place — no drift, no flip.' },
                  { icon: Shield, title: 'Algae-Free Design', desc: 'No hollow cavities or fill ports = nothing to grow inside.' },
                  { icon: Waves, title: 'No Fill Required', desc: 'Skip the sand and water steps competitors rely on.' },
                  { icon: Sparkles, title: 'Cleaner. Safer.', desc: 'Sanitary and maintenance-free, season after season.' },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300" data-testid={`ballast-feature-${i}`}>
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                      <f.icon size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-white mb-1">{f.title}</h3>
                      <p className="text-sm text-gray-400 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/products?category=loungers"
                data-testid="ballast-cta-shop-loungers"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-2xl hover:shadow-cyan-500/30 transform hover:scale-105 transition-all duration-300"
              >
                <span>Explore Loungers</span>
                <ArrowRight size={20} />
              </Link>
            </div>

            <div className="lg:col-span-5 order-1 lg:order-2">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-cyan-400 to-blue-600 rounded-3xl blur-2xl opacity-30" />
                <div className="relative aspect-[3/2] rounded-3xl overflow-hidden shadow-2xl border border-white/10">
                  <img
                    src={imgUrl("https://customer-assets.emergentagent.com/job_furniture-studio-3/artifacts/aflm330q_SPL33100WH%20Pool_Scene_v1_v4%281%29.jpg")}
                    alt="Tenjam in-pool lounger anchored on a sun shelf with the patent-pending Ballast Stabilization System"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-sm font-medium text-cyan-300 uppercase tracking-wider mb-1">Built for Performance</p>
                    <p className="text-white text-base">Commercial-grade, UV-stable, antimicrobial — built to outperform and outlast.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Featured Products</h2>
            <p className="text-base sm:text-xl text-gray-600">Our most popular in-pool furniture</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8 mb-8 sm:mb-10">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/products"
              className="inline-flex items-center px-6 sm:px-8 py-3 bg-gray-900 text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors duration-300 space-x-2"
            >
              <span>View All Products</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-14 sm:py-20 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Why Choose Rezvani?</h2>
            <p className="text-base sm:text-xl text-gray-600">Excellence in every detail</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8">
            {[
              {
                icon: Award,
                title: 'Premium Quality',
                description: 'Premium built and expertly crafted with commercial-grade materials, built to last',
              },
              {
                icon: Palette,
                title: 'Custom Colors',
                description: 'Made-to-order options to match your pool perfectly',
              },
              {
                icon: Shield,
                title: 'Weather Resistant',
                description: 'UV resistant and built to withstand all weather conditions',
              },
              {
                icon: Waves,
                title: 'Easy Installation',
                description: 'Simple setup with adjustable risers for various water depths',
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <feature.icon size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">What Our Customers Say</h2>
            <p className="text-base sm:text-xl text-gray-600">Join thousands of satisfied pool owners</p>
          </div>

          <div className="relative bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl p-6 sm:p-12 shadow-xl">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={24} className="fill-amber-400 text-amber-400 mx-1" />
                ))}
              </div>
              <p className="text-base sm:text-xl text-gray-700 mb-6 sm:mb-8 leading-relaxed italic">
                "{testimonials[currentTestimonial].text}"
              </p>
              <div className="flex items-center justify-center space-x-4">
                <img
                  src={testimonials[currentTestimonial].image}
                  alt={testimonials[currentTestimonial].name}
                  className="w-16 h-16 rounded-full border-4 border-white shadow-md"
                />
                <div className="text-left">
                  <p className="font-bold text-gray-900">{testimonials[currentTestimonial].name}</p>
                  <p className="text-gray-600">{testimonials[currentTestimonial].location}</p>
                </div>
              </div>
            </div>

            {/* Pagination Dots */}
            <div className="flex justify-center space-x-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentTestimonial ? 'w-8 bg-cyan-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Become a Dealer */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-gray-900 via-slate-900 to-cyan-950" id="become-a-dealer">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-semibold text-cyan-300 uppercase tracking-[0.2em] mb-3">Partner With Us</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Become a Dealer</h2>
            <p className="text-base sm:text-lg text-gray-300">
              Add premium, commercial-grade in-pool furniture to your lineup. Submit your details and our team will reach out.
            </p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-10 backdrop-blur-sm">
            <DealerForm dark />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="relative py-24 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/3068519/pexels-photo-3068519.jpeg?w=1920&q=80')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-900/70" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Upgrade Your Pool?</h2>
          <p className="text-xl mb-10 text-gray-200">
            Get a free consultation and custom quote for your pool furniture needs
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-lg font-semibold rounded-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 space-x-2"
          >
            <span>Get Free Quote</span>
            <ArrowRight size={22} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
